import React from "react";
import { SSHErrorCode } from "../../types/ssh-errors";

interface StatusBadgeProps {
  isConnected: boolean;
  isReconnecting?: boolean;
  connectionError: string | null;
  errorCode?: SSHErrorCode;
  sessionClosed?: boolean;
  isOnline: boolean;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  isConnected,
  isReconnecting,
  connectionError,
  errorCode,
  sessionClosed,
  isOnline,
}) => {
  const getErrorMessage = () => {
    switch (errorCode) {
      case SSHErrorCode.SESSION_NOT_FOUND:
        return "Session nicht gefunden";
      case SSHErrorCode.CONNECTION_FAILED:
        return "Verbindung fehlgeschlagen";
      case SSHErrorCode.STREAM_ERROR:
        return "Stream-Fehler";
      case SSHErrorCode.NOT_ESTABLISHED:
        return "Nicht verbunden";
      default:
        return "Fehler";
    }
  };

  const getStatusColor = () => {
    if (!isOnline)
      return {
        bg: "rgba(107, 114, 128, 0.1)",
        border: "rgba(107, 114, 128, 0.2)",
        text: "#6b7280",
      };
    if (sessionClosed)
      return {
        bg: "rgba(156, 163, 175, 0.1)",
        border: "rgba(156, 163, 175, 0.2)",
        text: "#9ca3af",
      };
    if (isConnected)
      return {
        bg: "rgba(16, 185, 129, 0.1)",
        border: "rgba(16, 185, 129, 0.2)",
        text: "#10b981",
      };
    if (isReconnecting)
      return {
        bg: "rgba(245, 158, 11, 0.1)",
        border: "rgba(245, 158, 11, 0.2)",
        text: "#f59e0b",
      };
    if (!connectionError)
      return {
        bg: "rgba(245, 158, 11, 0.1)",
        border: "rgba(245, 158, 11, 0.2)",
        text: "#f59e0b",
      };
    return {
      bg: "rgba(239, 68, 68, 0.1)",
      border: "rgba(239, 68, 68, 0.2)",
      text: "#ef4444",
    };
  };

  const colors = getStatusColor();

  const style: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "3px 8px",
    borderRadius: "16px",
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
  };

  const dotStyle: React.CSSProperties = {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    backgroundColor: colors.text,
    boxShadow: `0 0 6px ${colors.bg.replace("0.1", "0.6")}`,
    ...(!isConnected &&
      (isReconnecting ?? (!connectionError && !sessionClosed)) &&
      isOnline && { animation: "pulse 2s infinite" }),
  };

  const textStyle: React.CSSProperties = {
    color: colors.text,
    fontSize: "11px",
    fontWeight: 500,
    letterSpacing: "0.02em",
  };

  return (
    <>
      <div style={style} className="status-badge">
        <div style={dotStyle} className="status-badge-dot" />
        <span style={textStyle} className="status-badge-text">
          {!isOnline
            ? "Offline"
            : sessionClosed
              ? "Session beendet"
              : isConnected
                ? "Verbunden"
                : isReconnecting
                  ? "Verbindung wird wiederhergestellt..."
                  : connectionError
                    ? getErrorMessage()
                    : "Verbinde..."}
        </span>
      </div>
      <style>
        {`
          @media (max-width: 480px) {
            .status-badge {
              padding: 0 !important;
              min-width: auto !important;
              background-color: transparent !important;
              border: none !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            
            .status-badge-text {
              display: none !important;
            }
            
            .status-badge-dot {
              width: 10px !important;
              height: 10px !important;
              margin: 0 !important;
            }
          }
          
          @media (max-width: 767px) and (min-width: 481px) {
            .status-badge-text {
              font-size: 10px !important;
            }
          }
        `}
      </style>
    </>
  );
};
